import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AuthAxios from '../../axios';
import { GiftProductCard } from '../../components/coupon/GiftProductCard';
import '../../components/coupon/gift-product-card.css';
import { Icon } from '../../components/header/draft/Icon';
import { AmbientBackground } from '../../components/layout/AmbientBackground';
import { GlassSurface } from '../../components/profile/GlassSurface';
import '../../components/profile/profile-glass.css';
import '../../components/settings/settings-glass.css';
import { AuthStateContext } from '../../contexts/AuthContext';
import { ModalContext } from '../../contexts/ModalContext';
import type { Product } from '../../types';
import {
  formatGiftPriceKrw,
  getGiftStandardOrderPricing,
  giftProductFromApiProduct,
  resolveGiftSalePriceKrw,
} from '../../utils/gift/mapGiftApiProduct';
import { transLn } from '../../utils/transLn';
import FailedPaymentModal from './PaymentFailedModal';
import './gift-payment-page.css';

declare const window: typeof globalThis & {
  IMP: any;
};

function buildGiftCouponShareUrl(code: string): string {
  return `${window.location.origin}/app/setting?tab=coupon&giftcode=${encodeURIComponent(code)}`;
}

export default function GiftPaymentPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const gift = location.state?.gift as Product | undefined;
  const navigate = useNavigate();
  const { handleModal } = useContext(ModalContext);
  const { user } = useContext(AuthStateContext);
  const [loading, setLoading] = useState(false);

  const { IMP } = window;
  const giftCard = gift ? giftProductFromApiProduct(gift) : null;
  const salePriceKrw = gift ? resolveGiftSalePriceKrw(gift) : 0;
  const priceLabel = gift ? formatGiftPriceKrw(salePriceKrw) : '';
  const giftOrderPricing = gift ? getGiftStandardOrderPricing(gift) : null;

  useEffect(() => {
    if (!gift) {
      navigate('/app/gift/shop', { replace: true });
    }
  }, [gift, navigate]);

  const handlePostPayment = (res: unknown) => {
    setLoading(true);
    AuthAxios.post('/port-one/gift', res)
      .then((resolve) => {
        navigate('/app/gift/history', { replace: true });
        handleModal(<SuccessGiftPaymentModal info={resolve.data} />);
      })
      .catch(() => {
        handleModal(<FailedPaymentModal />);
      })
      .finally(() => setLoading(false));
  };

  const onClickpostPaypalPayment = (product: Product) => {
    IMP.init(process.env.REACT_APP_IMP_CODE);
    IMP.loadUI(
      'paypal-spb',
      {
        pg: 'paypal_v2.LU2S3GXCDJ6TJ',
        pay_method: 'paypal',
        amount: resolveGiftSalePriceKrw(product),
        name: '페이팔 결제 수단 등록',
      },
      function (rsp: { error_code?: string; customer_uid?: string }) {
        if (!rsp.error_code && rsp.customer_uid) {
          AuthAxios.put('/paymentMethod/card2', { customerUid: rsp.customer_uid }).catch(console.error);
        }
      },
    );
  };

  const onClickpostPayment = (product: Product) => {
    if (!window.IMP) return;

    IMP.init(process.env.REACT_APP_IMP_CODE);
    IMP.request_pay(
      {
        pg: `danal_tpay.${process.env.REACT_APP_IMP_MID}`,
        pay_method: 'card',
        name: transLn(product.description),
        merchant_uid: uuidv4(),
        amount: resolveGiftSalePriceKrw(product),
        buyer_email: user?.email,
        buyer_name: user?.nickName,
        custom_data: {
          userId: user?.id,
          productId: product.id,
        },
      },
      function (rsp: Record<string, unknown> & { success?: boolean }) {
        if (rsp.success) {
          handlePostPayment(rsp);
        }
      },
    );
  };

  if (!gift || !giftCard) {
    return null;
  }

  return (
    <div className="gift-payment-page soundpill-profile-view">
      <AmbientBackground />
      <div className="gift-payment-page__scroll">
        <button
          type="button"
          onClick={() => navigate('/app/gift/shop')}
          className="gift-payment-page__back"
        >
          <Icon name="arrow_back" className="text-xl" />
          {t('giftPaymentPage.back')}
        </button>

        <header className="gift-payment-page__hero">
          <h1 className="gift-payment-page__title">{t('giftPaymentPage.sendPremiumAsGift')}</h1>
          <p className="gift-payment-page__lead">{t('giftPaymentPage.checkoutLead')}</p>
        </header>

        <div className="gift-payment-page__card-wrap" aria-hidden>
          <GiftProductCard product={giftCard} />
        </div>

        <GlassSurface className="px-5 py-4 mb-4 w-full" rounded="2xl">
          <span className="gift-payment-page__panel-label">{t('giftPaymentPage.accountForGift')}</span>
          <p className="gift-payment-page__account-email">{user?.email}</p>
        </GlassSurface>

        <GlassSurface className="px-5 py-5 w-full" rounded="2xl">
          <span className="gift-payment-page__panel-label">{t('giftPaymentPage.orderSummary')}</span>
          <div className="gift-payment-page__summary-row">
            <span className="gift-payment-page__summary-name">{transLn(gift.name)}</span>
            {giftOrderPricing ? (
              <div className="gift-payment-page__summary-prices">
                <span className="gift-payment-page__summary-price gift-payment-page__summary-price--original">
                  {formatGiftPriceKrw(giftOrderPricing.originalPrice)}
                </span>
                <span className="gift-payment-page__summary-price gift-payment-page__summary-price--discounted">
                  {t('giftPaymentPage.discountApplied', {
                    percent: giftOrderPricing.discountPercent,
                    price: formatGiftPriceKrw(giftOrderPricing.salePrice),
                  })}
                </span>
              </div>
            ) : (
              <span className="gift-payment-page__summary-price">{priceLabel}</span>
            )}
          </div>
          <div className="gift-payment-page__total">
            <strong>{t('giftPaymentPage.totalAmount')}</strong>
            <span className="gift-payment-page__total-amount">{priceLabel}</span>
          </div>
        </GlassSurface>

        <div className="gift-payment-page__actions">
          <button
            type="button"
            disabled={loading}
            onClick={() => onClickpostPayment(gift)}
            className="gift-payment-page__btn gift-payment-page__btn--primary"
          >
            {loading ? t('giftPaymentPage.paymentProcessing') : t('giftPaymentPage.payWithKoreanCard')}
          </button>
          <div className="portone-ui-container" data-portone-ui-type="paypal-spb" />
          <button
            type="button"
            disabled={loading}
            onClick={() => onClickpostPaypalPayment(gift)}
            className="gift-payment-page__btn gift-payment-page__btn--secondary"
          >
            {t('giftPaymentPage.payForGiftCertificate')}
          </button>
        </div>

        <ul className="gift-payment-page__reassure">
          <li className="gift-payment-page__reassure-title">{t('giftPaymentPage.secureTransactions')}</li>
          <li>{t('giftPaymentPage.giftCertificateBenefits')}</li>
          <li>{t('giftPaymentPage.noAutomaticRenewal')}</li>
          <li>{t('giftPaymentPage.checkGiftDetails')}</li>
        </ul>
      </div>
    </div>
  );
}

type SuccessGiftPaymentModalProps = {
  info: { code?: string };
};

function SuccessGiftPaymentModal({ info }: SuccessGiftPaymentModalProps) {
  const { handleModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const code = info?.code ?? '';

  const handleShare = async () => {
    const shareUrl = buildGiftCouponShareUrl(code);
    const shareText = `${t('gift.couponCode')}: ${code}\n${shareUrl}`;

    handleModal();

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: t('giftPaymentPage.sendPremiumAsGift'),
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="gift-payment-success-modal glass-surface refractive-border rounded-2xl">
      <h2 className="gift-payment-success-modal__title">{t('giftPaymentPage.successTitle')}</h2>
      <p className="gift-payment-success-modal__lead">{t('giftPaymentPage.successSubtitle')}</p>
      <p className="gift-payment-success-modal__code-label">{t('gift.couponCode')}</p>
      <code className="gift-payment-success-modal__code">{code}</code>
      <div className="gift-payment-success-modal__actions">
        <button
          type="button"
          onClick={handleShare}
          className="gift-payment-page__btn gift-payment-page__btn--primary"
        >
          <span className="inline-flex items-center justify-center gap-2">
            <Icon name="share" className="text-lg" />
            {t('giftPaymentPage.shareCoupon')}
          </span>
        </button>
        <button type="button" onClick={() => handleModal()} className="gift-payment-page__btn gift-payment-page__btn--secondary">
          {t('registCardModal.confirm')}
        </button>
      </div>
    </div>
  );
}
